import React, { useState, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Paper, Grid, InputAdornment, TablePagination, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { textFieldStyles } from '../../Login/LoginPageStyles';
import TaskTimesheetDialog from './TaskTimesheetDialog';


const ETasks = ({ user }) => {
  const [tasksData, setTasksData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [page, setPage] = useState(0); // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);


  useEffect(() => {
    // Set current date
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    setCurrentDate(today.toLocaleDateString(undefined, options));
  }, []);

  useEffect(() => {
    if (user?.id) {
      // Fetch tasks for the logged-in user
      const fetchTasks = async () => {
        const assignedTasksQuery = query(
          collection(db, 'assignedTasks'),
          where('employeeId', '==', user.id) // Filter tasks based on employeeId
        );

        const unsubscribe = onSnapshot(assignedTasksQuery, (snapshot) => {
          const tasks = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setTasksData(tasks);
        });

        // Cleanup on unmount
        return () => unsubscribe();
      };

      fetchTasks();
    }
  }, [user]);



  const handleCompleteTask = async (taskId) => {
    try {
      // Create a reference to the task document
      const taskDocRef = doc(db, 'assignedTasks', taskId);

      // Update the document's status to "completed"
      await updateDoc(taskDocRef, {
        status: 'completed',
      });

      // Update the local state to reflect changes
      const updatedTasks = tasksData.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            status: 'completed',
          };
        }
        return task;
      });
      setTasksData(updatedTasks);
    } catch (error) {
      console.error('Error updating task: ', error);
    }
  };

  const filteredTasks = tasksData.filter((task) =>
    (task.status?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (task.assignedBy?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (task.taskName?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (task.taskId?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
    (task.date?.includes(searchTerm) || '')
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  const handleEditClick = (task) => {
    setSelectedTask(task);
    setIsDialogOpen(true);
  };
  

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          TimeSheet
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          padding: 3,
        }}
      >
        <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={8}>
            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by Task ID, Task, Assigned By, Status or Date"
              sx={{...textFieldStyles}}
            />
          </Grid>
          <Grid item xs={4}>
            <Box
              sx={{
                backgroundColor: '#F6F6F6',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CalendarTodayIcon sx={{ mr: 1 }} />
              <Typography variant="body1" sx={{ fontSize: '1rem' }}>{currentDate}</Typography>
            </Box>
          </Grid>
        </Grid>

        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ width: '50px' }}><b>Task ID<br />Date<br />Time</b></TableCell>
                <TableCell align="left"><b>Task</b></TableCell>
                <TableCell align="left"><b>Assigned By</b></TableCell>
                <TableCell align="center"><b>Status</b></TableCell>
                <TableCell align="center"><b>Work Hours</b></TableCell>
                <TableCell align="center"><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((task, index) => (
                <TableRow key={task.id}>
                  <TableCell align="left">ID:{task.taskId} <br /> {task.createdDate}</TableCell>
                  <TableCell align="left">{task.taskName}</TableCell>
                  <TableCell align="left">{task.assignedBy}</TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        color: 'white',
                        backgroundColor:
                          task.status === 'completed'
                            ? '#39B54A'
                            : task.status === 'ongoing'
                            ? '#FFA500'
                            : '#d3d3d3',
                      }}
                    >
                      {task.status}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{task.workHours}</TableCell>
                  <TableCell align="center" sx={{ width: '350px' }}>
                    <Button variant="contained" color="primary" sx={{ mb: 1 }} onClick={() => handleEditClick(task)}>
                         Edit
                    </Button>
                    <Button
                          variant="contained"
                          onClick={() => handleCompleteTask(task.id)}
                          sx={{ ml: 2, mb: 1, backgroundColor: '#39B54A', '&:hover': { backgroundColor: '#2c8f39' } }}
                    >
                      Completed
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 15]}
            component="div"
            count={filteredTasks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
        {selectedTask && (
      <TaskTimesheetDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        task={selectedTask}
        user={user}
      />
    )}
      </Box>
    </Box>
  );
};

export default ETasks;
